import { VStack, HStack, Text, Box, Input, IconButton, Badge, Alert, Grid } from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { User, X, AlertCircle, ChevronDown } from 'lucide-react'
import { CreateUnitMutationInput, MutationPartyInput, validateMutationShares, calculateSharesTotals } from '../../types/mutations'
import { useOwners } from '@/features/owners/hooks/useOwners'

interface Step2PartiesProps {
  formData: Partial<CreateUnitMutationInput>
  onUpdate: (data: Partial<CreateUnitMutationInput>) => void
  currentOwners: Array<{
    ownerId: string
    ownerName: string
    shareNumerator: number
    shareDenominator: number
    ownershipType?: 'full' | 'usufruct' | 'bare'
  }>
}

export function Step2Parties({ formData, onUpdate, currentOwners }: Step2PartiesProps) {
  const { data: allOwners = [] } = useOwners()

  const handleAddNewParty = () => {
    const newParties = [...(formData.new_parties || [])]
    newParties.push({
      owner_id: '',
      share_numerator: 1,
      share_denominator: 1,
      ownership_type: 'full',
    })
    onUpdate({ new_parties: newParties })
  }

  const handleUpdateNewParty = (index: number, updates: Partial<MutationPartyInput>) => {
    const newParties = [...(formData.new_parties || [])]
    newParties[index] = { ...newParties[index]!, ...updates }
    onUpdate({ new_parties: newParties })
  }

  const handleRemoveNewParty = (index: number) => {
    const newParties = [...(formData.new_parties || [])]
    newParties.splice(index, 1)
    onUpdate({ new_parties: newParties })
  }

  const handleUpdateFormerParty = (index: number, updates: Partial<MutationPartyInput>) => {
    const formerParties = [...(formData.former_parties || [])]
    formerParties[index] = { ...formerParties[index]!, ...updates }
    onUpdate({ former_parties: formerParties })
  }

  // Valider les parts
  const validation = validateMutationShares(
    formData.former_parties || [],
    formData.new_parties || []
  )

  // Calculer les totaux par type de propriété pour les nouveaux propriétaires
  const newTotals = calculateSharesTotals(formData.new_parties || [])

  return (
    <VStack align="stretch" gap={6}>
      <Text fontSize="md" color="gray.600">
        Définissez les anciens et nouveaux propriétaires avec leurs quotes-parts respectives.
      </Text>

      {/* Anciens propriétaires */}
      <Box>
        <Text fontSize="sm" fontWeight="bold" mb={3} color="red.700">
          Anciens propriétaires (parts cédées)
        </Text>
        <VStack align="stretch" gap={3}>
          {(formData.former_parties || []).map((party, index) => {
            const owner = currentOwners.find((o) => o.ownerId === party.owner_id)
            return (
              <Box
                key={index}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor="red.200"
                bg="red.50"
              >
                <VStack align="stretch" gap={2}>
                  <HStack justify="space-between">
                    <HStack gap={2}>
                      <User size={14} />
                      <Text fontSize="sm" fontWeight="medium">
                        {owner?.ownerName || 'Propriétaire inconnu'}
                      </Text>
                    </HStack>
                  </HStack>

                  <HStack gap={3}>
                    <Field.Root flex={1}>
                      <Field.Label fontSize="xs">Quote-part cédée</Field.Label>
                      <HStack gap={1}>
                        <Input
                          type="number"
                          min={0}
                          value={party.share_numerator}
                          onChange={(e) =>
                            handleUpdateFormerParty(index, {
                              share_numerator: Number(e.target.value) || 0,
                            })
                          }
                          size="sm"
                          textAlign="center"
                        />
                        <Text fontSize="sm" fontWeight="bold">/</Text>
                        <Input
                          type="number"
                          min={1}
                          value={party.share_denominator}
                          onChange={(e) =>
                            handleUpdateFormerParty(index, {
                              share_denominator: Number(e.target.value) || 1,
                            })
                          }
                          size="sm"
                          textAlign="center"
                        />
                      </HStack>
                    </Field.Root>

                    <Field.Root flex={1}>
                      <Field.Label fontSize="xs">Type de droit</Field.Label>
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={party.ownership_type || 'full'}
                          onChange={(e) =>
                            handleUpdateFormerParty(index, {
                              ownership_type: e.target.value as any,
                            })
                          }
                        >
                          <option value="full">Pleine propriété</option>
                          <option value="usufruct">Usufruit</option>
                          <option value="bare">Nue-propriété</option>
                        </NativeSelectField>
                        <Box
                          position="absolute"
                          right="3"
                          top="50%"
                          transform="translateY(-50%)"
                          pointerEvents="none"
                          color="gray.500"
                        >
                          <ChevronDown size={14} />
                        </Box>
                      </NativeSelectRoot>
                    </Field.Root>
                  </HStack>
                </VStack>
              </Box>
            )
          })}
        </VStack>
      </Box>

      {/* Nouveaux propriétaires */}
      <Box>
        <HStack justify="space-between" mb={3}>
          <Text fontSize="sm" fontWeight="bold" color="green.700">
            Nouveaux propriétaires (parts acquises)
          </Text>
          <button
            onClick={handleAddNewParty}
            style={{
              fontSize: '12px',
              padding: '4px 12px',
              background: '#48BB78',
              color: 'white',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            + Ajouter
          </button>
        </HStack>

        {(formData.new_parties || []).length === 0 ? (
          <Box p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200" bg="gray.50">
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Aucun nouveau propriétaire ajouté. Cliquez sur "+ Ajouter" pour commencer.
            </Text>
          </Box>
        ) : (
          <VStack align="stretch" gap={3}>
            {(formData.new_parties || []).map((party, index) => (
              <Box
                key={index}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor="green.200"
                bg="green.50"
              >
                <VStack align="stretch" gap={2}>
                  <HStack justify="space-between">
                    <Badge colorPalette="green" size="sm">
                      Nouveau #{index + 1}
                    </Badge>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      colorPalette="red"
                      onClick={() => handleRemoveNewParty(index)}
                      aria-label="Supprimer"
                    >
                      <X size={14} />
                    </IconButton>
                  </HStack>

                  <Field.Root>
                    <Field.Label fontSize="xs">Propriétaire</Field.Label>
                    <NativeSelectRoot position="relative">
                      <NativeSelectField
                        value={party.owner_id}
                        onChange={(e) =>
                          handleUpdateNewParty(index, { owner_id: e.target.value })
                        }
                        paddingRight="10"
                      >
                        <option value="">Sélectionner un propriétaire</option>
                        {allOwners.map((owner) => (
                          <option key={owner.id} value={owner.id}>
                            {`${owner.first_name || ''} ${owner.last_name || ''}`.trim() ||
                              'Sans nom'}
                          </option>
                        ))}
                      </NativeSelectField>
                      <Box
                        position="absolute"
                        right="3"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                      >
                        <ChevronDown size={14} />
                      </Box>
                    </NativeSelectRoot>
                  </Field.Root>

                  <HStack gap={3}>
                    <Field.Root flex={1}>
                      <Field.Label fontSize="xs">Quote-part acquise</Field.Label>
                      <HStack gap={1}>
                        <Input
                          type="number"
                          min={0}
                          value={party.share_numerator}
                          onChange={(e) =>
                            handleUpdateNewParty(index, {
                              share_numerator: Number(e.target.value) || 0,
                            })
                          }
                          size="sm"
                          textAlign="center"
                        />
                        <Text fontSize="sm" fontWeight="bold">/</Text>
                        <Input
                          type="number"
                          min={1}
                          value={party.share_denominator}
                          onChange={(e) =>
                            handleUpdateNewParty(index, {
                              share_denominator: Number(e.target.value) || 1,
                            })
                          }
                          size="sm"
                          textAlign="center"
                        />
                      </HStack>
                    </Field.Root>

                    <Field.Root flex={1}>
                      <Field.Label fontSize="xs">Type de droit</Field.Label>
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={party.ownership_type || 'full'}
                          onChange={(e) =>
                            handleUpdateNewParty(index, {
                              ownership_type: e.target.value as any,
                            })
                          }
                        >
                          <option value="full">Pleine propriété</option>
                          <option value="usufruct">Usufruit</option>
                          <option value="bare">Nue-propriété</option>
                        </NativeSelectField>
                        <Box
                          position="absolute"
                          right="3"
                          top="50%"
                          transform="translateY(-50%)"
                          pointerEvents="none"
                          color="gray.500"
                        >
                          <ChevronDown size={14} />
                        </Box>
                      </NativeSelectRoot>
                    </Field.Root>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>
        )}

        {/* Validation des totaux par type de propriété */}
        {(newTotals.full > 0 || newTotals.usufruct > 0 || newTotals.bare > 0) && (
          <Box mt={3}>
            <Text fontSize="xs" color="gray.600" mb={2} fontWeight="medium">
              Vérification des parts (chaque type doit totaliser 1.0000) :
            </Text>
            <Grid templateColumns="repeat(3, 1fr)" gap={2}>
              {newTotals.full > 0 && (
                <Box p={2} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.600" fontWeight="medium">
                    Pleine propriété
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color={Math.abs(newTotals.full - 1) < 0.0001 ? 'green.600' : 'orange.600'}>
                    {newTotals.full.toFixed(4)}
                  </Text>
                </Box>
              )}
              {newTotals.usufruct > 0 && (
                <Box p={2} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.600" fontWeight="medium">
                    Usufruit
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color={Math.abs(newTotals.usufruct - 1) < 0.0001 ? 'green.600' : 'orange.600'}>
                    {newTotals.usufruct.toFixed(4)}
                  </Text>
                </Box>
              )}
              {newTotals.bare > 0 && (
                <Box p={2} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.600" fontWeight="medium">
                    Nue-propriété
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color={Math.abs(newTotals.bare - 1) < 0.0001 ? 'green.600' : 'orange.600'}>
                    {newTotals.bare.toFixed(4)}
                  </Text>
                </Box>
              )}
            </Grid>
          </Box>
        )}
      </Box>

      {/* Validation */}
      {!validation.valid && (
        <Alert.Root status="error" size="sm">
          <HStack gap={2} align="start">
            <AlertCircle size={16} />
            <Box>
              <Alert.Title>Parts incompatibles</Alert.Title>
              <Alert.Description>{validation.error}</Alert.Description>
            </Box>
          </HStack>
        </Alert.Root>
      )}

      {validation.valid && formData.new_parties && formData.new_parties.length > 0 && (
        <Alert.Root status="success" size="sm">
          <Alert.Description>
            Les parts cédées et acquises correspondent. Vous pouvez passer à l'étape suivante.
          </Alert.Description>
        </Alert.Root>
      )}
    </VStack>
  )
}
